import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck, Clock3, Landmark, ShieldCheck, Trophy } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { EnquiryForm } from "@/components/EnquiryForm";
import { PartnerMark } from "@/components/PartnerMark";
import { useSEO } from "@/components/SEO";
import { getProduct } from "@/lib/products";

const BUSINESS_PARTNERS = [
  { name: "Godrej Finance", slug: "godrej-finance" },
  { name: "Bajaj Finserv", slug: "bajaj-finserv" },
  { name: "Credit Saison", slug: "credit-saison" },
  { name: "IDFC First Bank", slug: "idfc-first-bank" },
  { name: "Poonawalla Fincorp", slug: "poonawalla-fincorp" },
  { name: "InCred", slug: "incred" },
  { name: "L&T Finance", slug: "lt-finance" },
  { name: "Indifi", slug: "indifi" },
  { name: "Aditya Birla Capital", slug: "aditya-birla-capital" },
  { name: "DMI Finance", slug: "dmi-finance" },
];


const AUDIENCES = [
  { title: "Manufacturers & industrial units", text: "Unsecured machinery and expansion funding up to ₹2 Cr — without pledging the plant." },
  { title: "Traders & distributors", text: "Working capital that follows your purchase cycles, with dropline overdraft options from 8.5% p.a." },
  { title: "Services & professionals", text: "Cash-flow smoothing and growth capital against your P&L and banking behaviour." },
];

const SECTORS = [
  "Manufacturing", "Trading", "Wholesale", "Retail", "Services", "Logistics & Transport",
  "Auto Components", "Textiles & Apparel", "FMCG Distribution", "Pharma & Chemicals", "Engineering Goods",
];

const STORIES = [
  {
    quote: "As a first-generation business owner, banks felt like closed rooms. Loan Salaah knew exactly which door would open for my profile.",
    name: "Farah Sheikh",
    meta: "Mumbai · Business Loan",
  },
  {
    quote: "₹1.2 crore against our property, structured in eleven days. Calm, precise, no surprises — that's all you want at this size.",
    name: "Vikram Reddy",
    meta: "Hyderabad · Loan Against Property",
  },
  {
    quote: "They advised me not to take the bigger loan I asked for. That's the moment I decided to trust them with the smaller one.",
    name: "Meera Nair",
    meta: "Bengaluru · Personal Loan",
  },
];

const COMPARISON = [
  { label: "Advisor callback", us: "Within one working day", banks: "Queue at the branch" },
  { label: "Collateral", us: "Unsecured options up to ₹2 Cr", banks: "Often required" },
  { label: "Lender options", us: "10+ banks & NBFCs compete", banks: "One bank's rate card" },
  { label: "Paperwork", us: "Minimal, digital, guided", banks: "Branch visits, repeat visits" },
];

export default function BusinessLoanPage() {
  const product = getProduct("business-loan");

  useSEO({
    title: "Business Loan — Unsecured Working Capital up to ₹2 Cr | Loan Salaah",
    description: "Unsecured business loans and dropline overdrafts for GST-registered MSMEs. 10+ partner banks and NBFCs compete for your application. Indicative rates from 12.5% p.a.",
  });

  return (
    <div data-testid="business-loan-page" className="pb-0 pt-20">
      {/* ---------- hero ---------- */}
      <section className="relative overflow-hidden bg-concordia-ink text-ivory">
        <div className="noise-overlay" aria-hidden="true" />
        <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-brass/20 blur-3xl" aria-hidden="true" />
        <div className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 lg:grid-cols-12 lg:px-8 lg:py-24">
          <Reveal className="lg:col-span-7">
            <div className="flex items-center gap-4">
              <span className="brass-rule" aria-hidden="true" />
              <span className="font-mono text-[0.65rem] uppercase tracking-[0.3em] text-brass">Business Loans</span>
            </div>
            <h1 className="mt-6 font-serif text-4xl font-semibold leading-tight tracking-tight sm:text-5xl lg:text-6xl">
              Unsecured business loan in your account in{" "}
              <em className="text-brass">2 business days</em>
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-ivory/70">
              For GST-registered businesses with ₹1 Cr+ turnover and 2+ years of vintage. We place your application
              with the partner lender most likely to say yes at their sharpest slab.
            </p>
            <ul className="mt-7 flex flex-wrap gap-2.5">
              {["No hard credit check to enquire", "Indicative rates from 12.5% p.a.", "Collateral-free up to ₹2 Cr"].map((chip) => (
                <li key={chip} className="flex items-center gap-2 rounded-full border border-ivory/20 px-4 py-2 text-xs font-medium text-ivory/85">
                  <BadgeCheck className="h-3.5 w-3.5 text-brass" /> {chip}
                </li>
              ))}
            </ul>
            <dl className="mt-10 grid max-w-lg grid-cols-3 gap-6 border-t border-ivory/15 pt-8">
              {[
                ["10+", "partner banks & NBFCs"],
                ["₹2 Cr", "unsecured limit"],
                ["8", "loan & credit products"],
              ].map(([v, l]) => (
                <div key={l}>
                  <dt className="font-serif text-2xl font-semibold text-champagne sm:text-3xl">{v}</dt>
                  <dd className="mt-1 text-[0.7rem] uppercase tracking-wider text-ivory/50">{l}</dd>
                </div>
              ))}
            </dl>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5">
            <EnquiryForm prefill={{ loan_type: "Business Loan" }} />
          </Reveal>
        </div>
      </section>

      {/* ---------- who this is for ---------- */}
      <section className="bg-cream py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl">
            <div className="flex items-center gap-4">
              <span className="brass-rule" aria-hidden="true" />
              <span className="font-mono text-[0.65rem] uppercase tracking-[0.3em] text-brass">Who this is for</span>
            </div>
            <h2 className="mt-5 font-serif text-3xl font-semibold tracking-tight text-concordia sm:text-4xl">
              Working capital for businesses in motion.
            </h2>
          </Reveal>
          <Stagger className="mt-12 grid gap-5 md:grid-cols-3">
            {AUDIENCES.map((a) => (
              <StaggerItem key={a.title}>
                <div className="h-full rounded-3xl border border-sand bg-white p-7 shadow-[0_16px_40px_rgba(20,40,75,0.05)]" data-testid={`audience-${a.title.toLowerCase().replace(/[^a-z]+/g, "-").slice(0, 24)}`}>
                  <Landmark className="h-6 w-6 text-brass" />
                  <h3 className="mt-4 font-serif text-xl font-semibold text-concordia">{a.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate">{a.text}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* ---------- lending partners ---------- */}
      <section className="py-20 sm:py-28" data-testid="business-partners">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="mx-auto max-w-2xl text-center">
            <div className="flex items-center justify-center gap-4">
              <span className="brass-rule" aria-hidden="true" />
              <span className="font-mono text-[0.65rem] uppercase tracking-[0.3em] text-brass">Our lending partners</span>
              <span className="brass-rule" aria-hidden="true" />
            </div>
            <h2 className="mt-5 font-serif text-3xl font-semibold tracking-tight text-concordia sm:text-4xl">
              Your application, shopped across the market.
            </h2>
          </Reveal>
          <div className="mt-12 flex flex-wrap items-center justify-center gap-x-10 gap-y-8">
            {BUSINESS_PARTNERS.map((p) => (
              <PartnerMark key={p.slug} name={p.name} logo={`/partners/${p.slug}.png`} imgClassName="h-9 w-auto max-w-[130px] object-contain" textClassName="font-serif text-lg font-semibold text-concordia/70" />
            ))}
          </div>
        </div>
      </section>

      {/* ---------- sectors ---------- */}
      <section className="bg-cream py-20 sm:py-24" data-testid="sectors-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl">
            <div className="flex items-center gap-4">
              <span className="brass-rule" aria-hidden="true" />
              <span className="font-mono text-[0.65rem] uppercase tracking-[0.3em] text-brass">Sectors we serve</span>
            </div>
            <h2 className="mt-5 font-serif text-3xl font-semibold tracking-tight text-concordia sm:text-4xl">
              If your business moves goods or delivers work — we speak your language.
            </h2>
          </Reveal>
          <div className="mt-10 flex flex-wrap gap-2.5">
            {SECTORS.map((s) => (
              <span key={s} data-testid={`sector-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="rounded-full border border-sand bg-white px-5 py-2.5 text-sm font-medium text-charcoal transition-colors hover:border-brass hover:text-concordia">
                {s}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- comparison ---------- */}
      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <Reveal className="mx-auto max-w-2xl text-center">
            <div className="flex items-center justify-center gap-4">
              <span className="brass-rule" aria-hidden="true" />
              <span className="font-mono text-[0.65rem] uppercase tracking-[0.3em] text-brass">The honest comparison</span>
              <span className="brass-rule" aria-hidden="true" />
            </div>
            <h2 className="mt-5 font-serif text-3xl font-semibold tracking-tight text-concordia sm:text-4xl">
              With Loan Salaah vs. walking into a bank.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-12 overflow-hidden rounded-3xl border border-sand bg-white shadow-[0_20px_50px_rgba(20,40,75,0.06)]">
              <table className="w-full text-sm" data-testid="comparison-table">
                <thead>
                  <tr className="border-b border-sand text-left">
                    <th className="px-5 py-4 text-xs font-semibold uppercase tracking-wider text-slate">What matters</th>
                    <th className="bg-champagne/40 px-5 py-4 text-left font-serif text-base font-semibold text-concordia">With Loan Salaah</th>
                    <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate">Typical bank walk-in</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON.map((row) => (
                    <tr key={row.label} className="border-b border-sand/60 last:border-0">
                      <td className="px-5 py-4 text-xs font-semibold uppercase tracking-wider text-slate">{row.label}</td>
                      <td className="bg-champagne/40 px-5 py-4 font-medium text-concordia">{row.us}</td>
                      <td className="px-5 py-4 text-slate">{row.banks}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------- stories ---------- */}
      <section className="bg-concordia-ink py-20 text-ivory sm:py-28" data-testid="business-stories">
        <div className="noise-overlay" aria-hidden="true" />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl">
            <div className="flex items-center gap-4">
              <span className="brass-rule" aria-hidden="true" />
              <span className="font-mono text-[0.65rem] uppercase tracking-[0.3em] text-brass">What our customers say</span>
            </div>
            <h2 className="mt-5 font-serif text-3xl font-semibold tracking-tight sm:text-4xl">
              Real Loan Salaah customer experiences.
            </h2>
          </Reveal>
          <Stagger className="mt-12 grid gap-5 md:grid-cols-3">
            {STORIES.map((s) => (
              <StaggerItem key={s.name}>
                <figure className="flex h-full flex-col rounded-3xl border border-ivory/15 bg-ivory/[0.04] p-7">
                  <blockquote className="flex-1 font-serif text-lg leading-relaxed text-ivory/90">“{s.quote}”</blockquote>
                  <figcaption className="mt-6">
                    <p className="text-sm font-semibold text-champagne">{s.name}</p>
                    <p className="mt-0.5 text-xs text-ivory/55">{s.meta}</p>
                  </figcaption>
                </figure>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* ---------- faq ---------- */}
      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center">
            <div className="flex items-center justify-center gap-4">
              <span className="brass-rule" aria-hidden="true" />
              <span className="font-mono text-[0.65rem] uppercase tracking-[0.3em] text-brass">Common questions</span>
              <span className="brass-rule" aria-hidden="true" />
            </div>
            <h2 className="mt-5 font-serif text-3xl font-semibold tracking-tight text-concordia sm:text-4xl">
              Asked by every business owner we meet.
            </h2>
          </Reveal>
          <div className="mt-10 space-y-4">
            {product.faqs.map((f) => (
              <details key={f.q} data-testid={`business-faq-${f.q.toLowerCase().replace(/[^a-z]+/g, "-").slice(0, 30)}`}
                className="group rounded-2xl border border-sand bg-white p-5 open:border-brass/50">
                <summary className="cursor-pointer list-none font-serif text-lg font-semibold text-concordia marker:hidden">
                  {f.q}
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-slate">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- final cta ---------- */}
      <section className="bg-cream pb-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="relative overflow-hidden rounded-[2.5rem] bg-concordia-ink p-10 text-center text-ivory shadow-[0_40px_90px_rgba(20,40,75,0.35)] sm:p-16">
              <div className="noise-overlay" aria-hidden="true" />
              <div className="pointer-events-none absolute -left-24 -bottom-24 h-72 w-72 rounded-full bg-brass/20 blur-3xl" aria-hidden="true" />
              <div className="relative">
                <Trophy className="mx-auto h-8 w-8 text-brass" />
                <h2 className="mx-auto mt-5 max-w-2xl font-serif text-3xl font-semibold tracking-tight sm:text-4xl">
                  Check your eligibility in minutes — free, no commitments.
                </h2>
                <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-ivory/70">
                  One enquiry. Every relevant partner lender. An advisor calls you back within one working day.
                </p>
                <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                  <a href="#top" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                    data-testid="business-cta-check"
                    className="inline-flex items-center gap-2 rounded-full bg-ivory px-8 py-4 text-sm font-semibold text-concordia transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_14px_34px_rgba(0,0,0,0.35)]">
                    Check my eligibility <ArrowRight className="h-4 w-4" />
                  </a>
                  <Link to="/loan-comparison" data-testid="business-cta-compare"
                    className="inline-flex items-center gap-2 rounded-full border border-ivory/30 px-8 py-4 text-sm font-semibold text-ivory transition-colors hover:border-brass hover:text-brass">
                    <Clock3 className="h-4 w-4" /> Compare loan offers
                  </Link>
                </div>
                <p className="mt-6 flex items-center justify-center gap-1.5 text-[0.65rem] text-ivory/40">
                  <ShieldCheck className="h-3.5 w-3.5" /> Loan Salaah is a DSA marketplace — final approval, rates and terms rest with the lender.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
